<template>
  <div class="editable-cell" @mouseenter="showEdit = true" @mouseleave="showEdit = false">
    <!-- 显示模式 -->
    <div v-if="!isEditing" class="display-mode" @click="startEdit">
      <span class="display-value">{{ displayValue }}</span>
      <i 
        v-show="showEdit" 
        class="el-icon-edit edit-icon"
        :class="{ 'always-show': alwaysShowIcon }"
      ></i>
    </div>
    
    <!-- 编辑模式 -->
    <div v-else class="edit-mode">
      <el-input
        ref="editInput"
        v-model="editValue"
        :placeholder="placeholder"
        size="mini"
        @blur="saveEdit"
        @keyup.enter="saveEdit"
        @keyup.esc="cancelEdit"
        :class="{ 'error': hasError }"
      />
      <div v-if="hasError" class="error-message">{{ errorMessage }}</div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'EditableCell',
  props: {
    // 当前值
    value: {
      type: [String, Number],
      default: ''
    },
    // 显示值（用于格式化显示，如果不传则使用value）
    displayValue: {
      type: String,
      default: ''
    },
    // 输入类型：text, number, decimal
    inputType: {
      type: String,
      default: 'text'
    },
    // 数字类型的小数位数
    decimalPlaces: {
      type: Number,
      default: 2
    },
    // 占位符
    placeholder: {
      type: String,
      default: '请输入'
    },
    // 是否总是显示编辑图标
    alwaysShowIcon: {
      type: Boolean,
      default: false
    },
    // 自定义验证函数
    validator: {
      type: Function,
      default: null
    },
    // 是否禁用编辑
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isEditing: false,
      showEdit: false,
      editValue: '',
      hasError: false,
      errorMessage: ''
    }
  },
  computed: {
    computedDisplayValue() {
      return this.displayValue || this.value || ''
    }
  },
  methods: {
    // 开始编辑
    startEdit() {
      if (this.disabled) return
      
      this.isEditing = true
      this.editValue = this.value || ''
      this.hasError = false
      this.errorMessage = ''
      
      this.$nextTick(() => {
        if (this.$refs.editInput) {
          this.$refs.editInput.focus()
          this.$refs.editInput.select()
        }
      })
    },
    
    // 保存编辑
    saveEdit() {
      if (!this.validateInput()) {
        return
      }
      
      let finalValue = this.editValue
      
      // 数字类型处理
      if (this.inputType === 'number' || this.inputType === 'decimal') {
        finalValue = parseFloat(this.editValue) || 0
        if (this.inputType === 'decimal') {
          finalValue = parseFloat(finalValue.toFixed(this.decimalPlaces))
        }
      }
      
      // 如果值没有变化，直接取消编辑
      if (finalValue === this.value) {
        this.cancelEdit()
        return
      }
      
      // 触发更新事件
      this.$emit('update', finalValue, this.value)
      this.isEditing = false
    },
    
    // 取消编辑
    cancelEdit() {
      this.isEditing = false
      this.editValue = ''
      this.hasError = false
      this.errorMessage = ''
    },
    
    // 验证输入
    validateInput() {
      const value = this.editValue.trim()
      
      // 空值验证
      if (!value) {
        this.setError('不能为空')
        return false
      }
      
      // 数字类型验证
      if (this.inputType === 'number' || this.inputType === 'decimal') {
        const numberRegex = this.inputType === 'decimal' 
          ? new RegExp(`^\\d+(\\.\\d{1,${this.decimalPlaces}})?$`)
          : /^\d+$/
          
        if (!numberRegex.test(value)) {
          this.setError(this.inputType === 'decimal' 
            ? `请输入有效的数字（最多${this.decimalPlaces}位小数）`
            : '请输入有效的整数'
          )
          return false
        }
      }
      
      // 自定义验证
      if (this.validator) {
        const result = this.validator(value)
        if (result !== true) {
          this.setError(result || '输入不合法')
          return false
        }
      }
      
      this.clearError()
      return true
    },
    
    // 设置错误
    setError(message) {
      this.hasError = true
      this.errorMessage = message
    },
    
    // 清除错误
    clearError() {
      this.hasError = false
      this.errorMessage = ''
    }
  }
}
</script>

<style scoped lang="scss">
.editable-cell {
  position: relative;
  min-height: 32px;
  
  .display-mode {
    display: flex;
    align-items: center;
    justify-content: space-between;
    cursor: pointer;
    padding: 8px 12px;
    border-radius: 4px;
    transition: background-color 0.2s;
    
    &:hover {
      background-color: #f5f7fa;
    }
    
    .display-value {
      flex: 1;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    
    .edit-icon {
      margin-left: 8px;
      color: #909399;
      font-size: 14px;
      opacity: 0;
      transition: opacity 0.2s;
      
      &.always-show {
        opacity: 1;
      }
    }
    
    &:hover .edit-icon {
      opacity: 1;
    }
  }
  
  .edit-mode {
    .el-input {
      &.error {
        .el-input__inner {
          border-color: #f56c6c;
        }
      }
    }
    
    .error-message {
      color: #f56c6c;
      font-size: 12px;
      margin-top: 4px;
      line-height: 1;
    }
  }
}
</style> 